import clsx from 'clsx'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useToast } from '@/hooks/useToast'

/* blocks */
import Link from '@/atoms/Link/Link'
import Reveal from '../Reveal/Reveal'

/* assets */
import { ReactComponent as LinkIcon } from 'public/assets/icons/events/link.svg'
import { ReactComponent as TwitterIcon } from 'public/assets/icons/events/twitter.svg'
import { ReactComponent as FacebookIcon } from 'public/assets/icons/events/facebook.svg'

/* styles */
import styles from '../../_.module.css'

interface Props {
  class?: string
  content: StrapiSharedContentComponent[]
  testimonial: {
    id: string
    authorName: string
    authorDesignation: string
    authorCompany: string
    review: string
    authorImage: {
      data: {
        attributes: StrapiImageFile
      }
    }
    authorCompanyImage: {
      data: {
        attributes: StrapiImageFile
      }
    }
  }
  videoUrl?: string
  nextStory?: {
    slug: string
    title: string
  }
}

/** Static section copy for the testimonial video — the video itself is CMS
 *  content (testimonialVideoUrl); only this framing headline is designed. */
const VIDEO_SECTION = {
  id: 'experience',
  eyebrow: 'The experience',
  title: 'Why marketing leaders choose Pepper'
}

const EYEBROW_CLASSES =
  "inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#2563FF] before:h-0.5 before:w-[22px] before:rounded before:bg-[#2563FF] before:content-['']"

const PROSE_CLASSES = clsx(
  'prose max-w-none text-[#46517A]',
  'prose-headings:font-heading prose-headings:tracking-tight prose-headings:text-[#1E2178]',
  'prose-h3:text-lg prose-strong:text-[#131C4F] prose-a:text-[#2563FF]',
  // CMS list items wrap their text in <p>, which stacks paragraph margins on
  // top of the li margins — collapse them to the design's 10px rhythm
  'prose-li:mt-0 prose-li:mb-2.5 prose-li:marker:text-[#2563FF] [&_li>p]:my-0',
  'prose-blockquote:border-l-[3px] prose-blockquote:border-l-[#2563FF] prose-blockquote:font-medium prose-blockquote:text-[#131C4F]',
  'prose-figure:rounded-3xl prose-figure:border prose-figure:border-[#DEE6F6] prose-figure:bg-white prose-figure:p-4',
  'prose-figure:shadow-[0_2px_10px_rgba(19,28,79,0.06),0_18px_44px_rgba(19,28,79,0.08)]',
  'prose-figcaption:pt-3 prose-figcaption:text-center prose-figcaption:text-xs prose-figcaption:text-[#7C87B4]',
  'prose-img:rounded-2xl prose-img:bg-[#EEF3FC] [&_figure_img]:my-0',
  '[&_iframe]:aspect-video [&_iframe]:h-auto [&_iframe]:w-full [&_iframe]:rounded-3xl [&_iframe]:bg-[#0B1540]',
  '[&_span[style]]:text-inherit!'
)

function CaseStudyContent({ content, nextStory, ...props }: Props) {
  const [activeSection, setActiveSection] = useState<string | null>('')
  const { toast, showToast } = useToast()
  const router = useRouter()

  const tocItems = [
    ...(props.videoUrl
      ? [{ id: VIDEO_SECTION.id, title: VIDEO_SECTION.title }]
      : []),
    ...content.map(item => ({ id: `${item.id}`, title: item.title }))
  ]

  const links = `${process.env.NEXT_PUBLIC_BASE_URL}` + router.asPath
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(links)
      showToast({
        title: 'Link copied to clipboard',
        type: 'success',
        swipeDirection: 'up'
      })
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    const sections = document.querySelectorAll(
      '#case-study-article section[id^="section-"]'
    )
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id)
          }
        })
      },
      { root: null, rootMargin: '-30px 0px 0px 0px' }
    )

    sections.forEach(section => observer.observe(section))
    return () => observer.disconnect()
  }, [])

  return (
    <section className="container | py-12 md:py-16">
      <div
        className={clsx(
          props.class,
          'grid grid-cols-1 items-start gap-10 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-14'
        )}
      >
        {/* sticky rail */}
        <aside className="lg:sticky lg:top-24">
          <div className="rounded-3xl border border-[#DEE6F6] bg-[#F4F7FE] p-6 pb-5">
            <h2 className="mb-3.5 text-[13px] font-bold uppercase tracking-[0.12em] text-[#1E2178]">
              In this story
            </h2>
            {tocItems.map(item => (
              <Link
                key={item.id}
                href={`#section-${item.id}`}
                onClick={() => setActiveSection(`section-${item.id}`)}
                className={clsx(
                  'block border-l-2 py-2 pl-4 text-sm leading-snug transition-colors hover:border-[#2563FF] hover:text-[#2563FF]',
                  activeSection === `section-${item.id}`
                    ? 'border-[#2563FF] font-semibold text-[#2563FF]'
                    : 'border-[#DEE6F6] font-medium text-[#46517A]'
                )}
              >
                {item.title}
              </Link>
            ))}
          </div>

          <div className="mt-4 rounded-3xl bg-[#1F2179] p-6 text-white">
            <p className="mb-4 text-sm leading-normal text-[#C3CDF2]">
              See what a search-intelligent content engine could do for your
              funnel.
            </p>
            <Link
              href="/contact-us/"
              className="inline-flex w-full items-center justify-center rounded-full bg-[#2563FF] px-5 py-3.5 text-xs font-bold uppercase tracking-wider text-white shadow-[0_10px_26px_rgba(37,99,255,0.3)] transition hover:bg-[#1F4FE0]"
            >
              Book a Demo
            </Link>
          </div>

          <div className="mt-5 flex items-center gap-4 px-2">
            <span className="text-[13px] font-medium text-[#7C87B4]">
              Share this case study
            </span>
            <div className="flex items-center gap-4 text-[#131C4F]">
              <a
                href={`https://www.facebook.com/sharer/sharer.php?quote=${SHARE_TEXT}&u=${URL}`}
                target="_blank"
                rel="noreferrer noopener"
                title="Facebook"
              >
                <FacebookIcon />
              </a>
              <a
                href={`https://twitter.com/share?text=${SHARE_TEXT}`}
                target="_blank"
                rel="noreferrer noopener"
                title="Twitter"
              >
                <TwitterIcon width="25" height="25" />
              </a>
              <button onClick={copyLink} className="shareicon">
                <LinkIcon />
              </button>
              {toast}
            </div>
          </div>
        </aside>

        {/* article */}
        <article id="case-study-article" className="max-w-[720px]">
          {props.testimonial ? (
            <Reveal className="mb-14">
              <div className="relative flex flex-col items-center gap-8 overflow-hidden rounded-4xl bg-linear-to-br from-[#F4F7FE] to-[#E9F0FC] p-7 text-center shadow-[0_2px_10px_rgba(19,28,79,0.06),0_18px_44px_rgba(19,28,79,0.08)] md:flex-row md:p-10 md:text-left">
                <span
                  aria-hidden
                  className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-[46%_54%_58%_42%/52%_46%_54%_48%] bg-blue-10"
                />
                {props.testimonial.authorImage?.data?.attributes?.url ? (
                  <div className="relative shrink-0">
                    <span
                      aria-hidden
                      className="absolute -bottom-3 -right-3 left-4 top-4 rounded-[26px_26px_26px_10px] bg-[#1F2179]"
                    />
                    <Image
                      src={props.testimonial.authorImage.data.attributes.url}
                      alt={
                        props.testimonial.authorImage.data.attributes
                          .alternativeText ?? props.testimonial.authorName
                      }
                      width={132}
                      height={150}
                      className="relative h-[150px] w-[132px] rounded-[26px_26px_10px_26px] bg-pastel-blue-300 object-cover object-top"
                    />
                  </div>
                ) : null}
                <div className="relative">
                  <div
                    className={clsx(
                      styles.testimonialReview,
                      'mb-3.5 text-lg font-medium italic leading-normal text-[#131C4F] md:text-[19px]'
                    )}
                    dangerouslySetInnerHTML={{
                      __html: props.testimonial.review
                    }}
                  />
                  <div className="text-[15px] font-bold text-[#1E2178]">
                    {props.testimonial.authorName}
                  </div>
                  <div className="text-[13px] text-[#46517A]">
                    {`${props.testimonial.authorDesignation}, ${props.testimonial.authorCompany}`}
                  </div>
                </div>
              </div>
            </Reveal>
          ) : null}

          {props.videoUrl ? (
            <Reveal className="mb-14">
              <section
                id={`section-${VIDEO_SECTION.id}`}
                className="scroll-mt-24"
              >
                <span className={EYEBROW_CLASSES}>{VIDEO_SECTION.eyebrow}</span>
                <h2 className="font-heading mb-5 mt-3 text-2xl font-extrabold tracking-tight text-[#131C4F] md:text-[32px]">
                  {VIDEO_SECTION.title}
                </h2>
                <iframe
                  title="Case Study Video Iframe"
                  src={props.videoUrl}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="aspect-video w-full rounded-3xl bg-[#0B1540] shadow-[0_10px_30px_rgba(19,28,79,0.12),0_40px_90px_rgba(19,28,79,0.16)]"
                />
              </section>
            </Reveal>
          ) : null}

          {content.map(item => (
            <section
              id={`section-${item.id}`}
              key={item.id}
              className="mb-14 scroll-mt-24"
            >
              <h2 className="font-heading mb-4 text-2xl font-extrabold tracking-tight text-[#131C4F] md:text-[32px]">
                {item.title}
              </h2>
              {item.subTitle ? (
                <p className="mb-4 text-[17px] leading-relaxed text-[#46517A]">
                  {item.subTitle}
                </p>
              ) : null}
              <div
                className={PROSE_CLASSES}
                dangerouslySetInnerHTML={{
                  __html: item.description
                }}
              />
            </section>
          ))}

          {nextStory ? (
            <Reveal>
              <Link
                href={`/case-study/${nextStory.slug}/`}
                className="flex flex-wrap items-center justify-between gap-5 rounded-[26px] border border-[#DEE6F6] bg-white px-7 py-6 shadow-[0_2px_10px_rgba(19,28,79,0.06),0_18px_44px_rgba(19,28,79,0.08)] transition hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(19,28,79,0.12),0_40px_90px_rgba(19,28,79,0.16)]"
              >
                <div className="min-w-0 flex-1">
                  <span className="mb-1.5 block text-[11.5px] font-bold uppercase tracking-[0.12em] text-[#2563FF]">
                    Next story
                  </span>
                  <b className="font-heading text-lg font-extrabold text-[#131C4F]">
                    {nextStory.title}
                  </b>
                </div>
                <span
                  aria-hidden
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#1E2178] text-lg text-white"
                >
                  →
                </span>
              </Link>
            </Reveal>
          ) : null}
        </article>
      </div>
    </section>
  )
}

const URL = `${process.env.NEXT_PUBLIC_BASE_URL}`
const SHARE_TEXT =
  encodeURI(`Discover this customer's journey to organic marketing success!
${URL} @pepper__inc`)

export default CaseStudyContent
